import { of } from 'rxjs/observable/of';
import { Observable } from 'rxjs';
import { fromPromise } from 'rxjs/observable/fromPromise';
import { switchMap, catchError } from 'rxjs/operators';
import { ofType } from 'redux-observable';
import { Action } from 'redux-actions';

import {
	GET_OVERTIME_APPLICATIONS_CALL,
	POST_OVERTIME_APPLICATION_CALL,
	GET_OVERTIME_APPLICATION_INFO_CALL,
	GET_OVERTIME_APPLICATION_PAYMENT_METHODS_CALL,
	POST_OVERTIME_APPLICATION_PREVIEW_CALL,
	GET_OVERTIME_APPLICATION_DETAIL_CALL,
	POST_OVERTIME_APPLICATION_APPROVE_CALL,
	GET_OVERTIME_APPLICATION_CONFIRM_CALL,
	POST_OVERTIME_APPLICATION_CONFIRM_CALL,
	POST_OVERTIME_APPLICATION_WITHDRAW_CALL,
	GET_OVERTIME_APPLICATIONS_APPROVER_VIEW_CALL,
	POST_OVERTIME_APPLICATION_WITHDRAW_APPROVAL_CALL,
	POST_OVERTIME_APPLICATIONS_BATCH_APPROVE_CALL,
} from '@src/store/types/overtimeApplication';

import {
	getOvertimeApplicationsDone,
	getOvertimeApplicationsFail,
	postOvertimeApplicationDone,
	postOvertimeApplicationFail,
	getOvertimeApplicationInfoDone,
	getOvertimeApplicationInfoFail,
	getOvertimeApplicationPaymentMethodsDone,
	getOvertimeApplicationPaymentMethodsFail,
	postOvertimeApplicationPreviewDone,
	postOvertimeApplicationPreviewFail,
	getOvertimeApplicationDetailDone,
	getOvertimeApplicationDetailFail,
	postOvertimeApplicationApproveDone,
	postOvertimeApplicationApproveFail,
	getOvertimeApplicationConfirmDone,
	getOvertimeApplicationConfirmFail,
	postOvertimeApplicationConfirmDone,
	postOvertimeApplicationConfirmFail,
	postOvertimeApplicationWithdrawDone,
	postOvertimeApplicationWithdrawFail,
	getOvertimeApplicationsApproverViewDone,
	getOvertimeApplicationsApproverViewFail,
	postOvertimeApplicationWithdrawApprovalDone,
	postOvertimeApplicationWithdrawApprovalFail,
	postOvertimeApplicationsBatchApproveDone,
	postOvertimeApplicationsBatchApproveFail,
} from '@src/store/actions/overtimeApplication';
import {
	getOvertimeApplications,
	postOvertimeApplication,
	getOvertimeApplicationInfo,
	getOvertimeApplicationPaymentMethods,
	postOvertimeApplicationPreview,
	getOvertimeApplicationDetail,
	postOvertimeApplicationApprove,
	getOvertimeApplicationConfirm,
	postOvertimeApplicationConfirm,
	postOvertimeApplicationWithdraw,
	getOvertimeApplicationsApproverView,
	postOvertimeApplicationWithdrawApproval,
	postOvertimeApplicationsBatchApprove,
	getOvertimeApplicationsBatchApprove,
} from '@src/services/overtimeApplication';
import { pollBatchApprovalTask } from '@src/utils/batchApproval';

export const getOvertimeApplicationsEpic = (
	action$: Observable<Action>,
): Observable<Action> => action$.pipe(
	ofType(GET_OVERTIME_APPLICATIONS_CALL),
	switchMap(
		({ payload }) => fromPromise(getOvertimeApplications(payload)).pipe(
			switchMap(res => of(getOvertimeApplicationsDone({ res }))),
			catchError(error => of(getOvertimeApplicationsFail({ error }))),
		),
	),
);

export const postOvertimeApplicationEpic = (
	action$: Observable<Action>,
): Observable<Action> => action$.pipe(
	ofType(POST_OVERTIME_APPLICATION_CALL),
	switchMap(
		({ payload }) => fromPromise(postOvertimeApplication(payload)).pipe(
			switchMap(res => {
				if (payload.onSuccess) payload.onSuccess(res);
				return of(
					postOvertimeApplicationDone({ res }),
				);
			}),
			catchError(error => {
				if (payload.onError) payload.onError(error.response);
				return of(
					postOvertimeApplicationFail({ error }),
				);
			}),
		),
	),
);

export const getOvertimeApplicationInfoEpic = (
	action$: Observable<Action>,
): Observable<Action> => action$.pipe(
	ofType(GET_OVERTIME_APPLICATION_INFO_CALL),
	switchMap(
		({ payload }) => fromPromise(getOvertimeApplicationInfo(payload)).pipe(
			switchMap(res => of(getOvertimeApplicationInfoDone({ res }))),
			catchError(error => of(getOvertimeApplicationInfoFail({ error }))),
		),
	),
);

export const getOvertimeApplicationPaymentMethodsEpic = (
	action$: Observable<Action>,
): Observable<Action> => action$.pipe(
	ofType(GET_OVERTIME_APPLICATION_PAYMENT_METHODS_CALL),
	switchMap(
		({ payload }) => fromPromise(getOvertimeApplicationPaymentMethods(payload)).pipe(
			switchMap(res => of(getOvertimeApplicationPaymentMethodsDone({ res }))),
			catchError(error => of(getOvertimeApplicationPaymentMethodsFail({ error }))),
		),
	),
);

export const postOvertimeApplicationPreviewEpic = (
	action$: Observable<Action>,
): Observable<Action> => action$.pipe(
	ofType(POST_OVERTIME_APPLICATION_PREVIEW_CALL),
	switchMap(
		({ payload }) => fromPromise(postOvertimeApplicationPreview(payload)).pipe(
			switchMap(res => of(postOvertimeApplicationPreviewDone({ res }))),
			catchError(error => of(postOvertimeApplicationPreviewFail({ error }))),
		),
	),
);

export const getOvertimeApplicationDetailEpic = (
	action$: Observable<Action>,
): Observable<Action> => action$.pipe(
	ofType(GET_OVERTIME_APPLICATION_DETAIL_CALL),
	switchMap(
		({ payload }) => fromPromise(getOvertimeApplicationDetail(payload)).pipe(
			switchMap(res => {
				if (payload.onSuccess) payload.onSuccess(res);
				return of(getOvertimeApplicationDetailDone({ res }));
			}),
			catchError(error => {
				const { onError } = payload;
				if (typeof onError === 'function') onError(error.response);
				return of(getOvertimeApplicationDetailFail({ error }));
			}),
		),
	),
);

export const postOvertimeApplicationApproveEpic = (
	action$: Observable<Action>,
): Observable<Action> => action$.pipe(
	ofType(POST_OVERTIME_APPLICATION_APPROVE_CALL),
	switchMap(
		({ payload }) => fromPromise(postOvertimeApplicationApprove(payload)).pipe(
			switchMap(res => {
				if (payload.onSuccess) payload.onSuccess(res);
				return of(postOvertimeApplicationApproveDone({ res }));
			}),
			catchError(error => {
				if (payload.onError) payload.onError(error.response);
				return of(postOvertimeApplicationApproveFail({ error }));
			}),
		),
	),
);

export const getOvertimeApplicationConfirmEpic = (
	action$: Observable<Action>,
): Observable<Action> => action$.pipe(
	ofType(GET_OVERTIME_APPLICATION_CONFIRM_CALL),
	switchMap(
		({ payload }) => fromPromise(getOvertimeApplicationConfirm(payload)).pipe(
			switchMap(res => of(getOvertimeApplicationConfirmDone({ res }))),
			catchError(error => {
				if (payload.onError) payload.onError(error.response);
				return of(
					getOvertimeApplicationConfirmFail({ error }),
				);
			}),
		),
	),
);

export const postOvertimeApplicationConfirmEpic = (
	action$: Observable<Action>,
): Observable<Action> => action$.pipe(
	ofType(POST_OVERTIME_APPLICATION_CONFIRM_CALL),
	switchMap(
		({ payload }) => fromPromise(postOvertimeApplicationConfirm(payload)).pipe(
			switchMap(res => {
				if (payload.onSuccess) payload.onSuccess(res);
				return of(postOvertimeApplicationConfirmDone({ res }));
			}),
			catchError(error => {
				if (payload.onError) payload.onError(error.response);
				return of(postOvertimeApplicationConfirmFail({ error }));
			}),
		),
	),
);

export const postOvertimeApplicationWithdrawEpic = (
	action$: Observable<Action>,
): Observable<Action> => action$.pipe(
	ofType(POST_OVERTIME_APPLICATION_WITHDRAW_CALL),
	switchMap(
		({ payload }) => fromPromise(postOvertimeApplicationWithdraw(payload)).pipe(
			switchMap(res => {
				if (payload.onSuccess) payload.onSuccess(res);
				return of(postOvertimeApplicationWithdrawDone({ res }));
			}),
			catchError(error => {
				if (payload.onError) payload.onError(error.response);
				return of(postOvertimeApplicationWithdrawFail({ error }));
			}),
		),
	),
);

export const getOvertimeApplicationsApproverViewEpic = (
	action$: Observable<Action>,
): Observable<Action> => action$.pipe(
	ofType(GET_OVERTIME_APPLICATIONS_APPROVER_VIEW_CALL),
	switchMap(
		({ payload }) => fromPromise(getOvertimeApplicationsApproverView(payload)).pipe(
			switchMap(res => of(getOvertimeApplicationsApproverViewDone({ res }))),
			catchError(error => of(getOvertimeApplicationsApproverViewFail({ error }))),
		),
	),
);

export const postOvertimeApplicationWithdrawApprovalEpic = (
	action$: Observable<Action>,
): Observable<Action> => action$.pipe(
	ofType(POST_OVERTIME_APPLICATION_WITHDRAW_APPROVAL_CALL),
	switchMap(
		({ payload }) => fromPromise(postOvertimeApplicationWithdrawApproval(payload)).pipe(
			switchMap(res => {
				if (payload.onSuccess) payload.onSuccess(res);
				return of(postOvertimeApplicationWithdrawApprovalDone({ res }));
			}),
			catchError(error => {
				if (payload.onError) payload.onError(error.response);
				return of(postOvertimeApplicationWithdrawApprovalFail({ error }));
			}),
		),
	),
);

export const postOvertimeApplicationsBatchApproveEpic = (
	action$: Observable<Action>,
): Observable<Action> => action$.pipe(
	ofType(POST_OVERTIME_APPLICATIONS_BATCH_APPROVE_CALL),
	switchMap(
		({ payload }) => fromPromise(postOvertimeApplicationsBatchApprove(payload)).pipe(
			switchMap(task => pollBatchApprovalTask(() => getOvertimeApplicationsBatchApprove(task))),
			switchMap(res => {
				// 任務因系統錯誤中斷：比照 HTTP 錯誤，以 { data } 交給 onError
				if (res.status === 'failed') {
					if (payload.onError) payload.onError({ data: res });
					return of(postOvertimeApplicationsBatchApproveFail({ error: res }));
				}
				if (payload.onSuccess) payload.onSuccess(res);
				return of(postOvertimeApplicationsBatchApproveDone({ res }));
			}),
			catchError(error => {
				if (payload.onError) payload.onError(error.response);
				return of(postOvertimeApplicationsBatchApproveFail({ error }));
			}),
		),
	),
);
